/**
 * Two-player dominoes — double-six set, parameterized by mode.
 *
 *   - Block mode: deal 7 each, the remaining 14 sleep (unused). If you can't
 *     play, you pass. Two passes in a row → blocked → lower pip total wins.
 *   - Draw mode: deal 7 each, the remaining 14 form the boneyard. If you
 *     can't play, you must draw from the boneyard (one tile at a time)
 *     until you can play, OR the boneyard is empty — only then do you pass.
 *
 * Both modes share: highest-double-opens rule (heaviest-tile fallback if no
 * doubles dealt), match-on-end placement with doubles played by pip value,
 * empty-hand wins outright.
 */

import type { Domino } from './domino.js';
import { handPipTotal, isDouble, makeDomino, pipTotal } from './domino.js';
import {
  canPlayLeft,
  canPlayRight,
  emptyChain,
  playLeft as chainPlayLeft,
  playRight as chainPlayRight,
  type Chain,
} from './chain.js';
import { createDoubleSixSet } from './domino.js';
import { mulberry32 } from '../shuffle.js';

export type Player = 0 | 1;
export type Side = 'left' | 'right';
export type Result = 'empty' | 'blocked' | 'draw';
export type Mode = 'block' | 'draw';

export interface DominoGameState {
  mode: Mode;
  hands: [Domino[], Domino[]];
  chain: Chain;
  /** Unplayed tiles. Empty in block mode (the 14 leftovers are discarded). */
  boneyard: Domino[];
  turn: Player;
  passes: number;
  winner: Player | null;
  result: Result | null;
  /** The tile that must be played to open the game. Cleared once placed. */
  requiredOpener: { player: Player; tile: Domino } | null;
}

/** Backwards-compatible alias — older imports use BlockGameState. */
export type BlockGameState = DominoGameState;

export interface LegalPlay {
  domino: Domino;
  sides: Side[];
}

const HAND_SIZE = 7;

function shuffleSet(seed?: number): Domino[] {
  const set = createDoubleSixSet();
  const rand = seed === undefined ? Math.random : mulberry32(seed);
  const out = set.slice();
  for (let i = out.length - 1; i > 0; i--) {
    const j = Math.floor(rand() * (i + 1));
    const tmp = out[i]!;
    out[i] = out[j]!;
    out[j] = tmp;
  }
  return out;
}

function pickOpener(hands: [Domino[], Domino[]]): { player: Player; tile: Domino } {
  let bestDouble: { player: Player; tile: Domino; v: number } | null = null;
  for (const p of [0, 1] as const) {
    for (const d of hands[p]) {
      if (isDouble(d) && (!bestDouble || d.high > bestDouble.v)) {
        bestDouble = { player: p, tile: d, v: d.high };
      }
    }
  }
  if (bestDouble) return { player: bestDouble.player, tile: bestDouble.tile };

  // No doubles dealt — heaviest tile opens.
  let best: { player: Player; tile: Domino; v: number } | null = null;
  for (const p of [0, 1] as const) {
    for (const d of hands[p]) {
      const v = pipTotal(d);
      if (!best || v > best.v) best = { player: p, tile: d, v };
    }
  }
  return { player: best!.player, tile: best!.tile };
}

export interface DealOptions {
  mode: Mode;
  seed?: number;
}

export function dealGame(opts: DealOptions): DominoGameState {
  const deck = shuffleSet(opts.seed);
  const handA = deck.slice(0, HAND_SIZE);
  const handB = deck.slice(HAND_SIZE, HAND_SIZE * 2);
  const rest = deck.slice(HAND_SIZE * 2);
  const hands: [Domino[], Domino[]] = [handA, handB];
  const opener = pickOpener(hands);
  return {
    mode: opts.mode,
    hands,
    chain: emptyChain,
    boneyard: opts.mode === 'draw' ? rest : [],
    turn: opener.player,
    passes: 0,
    winner: null,
    result: null,
    requiredOpener: opener,
  };
}

export function dealBlockGame(seed?: number): DominoGameState {
  return dealGame({ mode: 'block', seed });
}

export function dealDrawGame(seed?: number): DominoGameState {
  return dealGame({ mode: 'draw', seed });
}

export function legalPlaysFor(state: DominoGameState, player: Player): LegalPlay[] {
  if (state.winner !== null) return [];
  // Forced opener: only that exact tile is legal, played to the right.
  if (state.requiredOpener && state.requiredOpener.player === player) {
    if (state.turn !== player) return [];
    return [{ domino: state.requiredOpener.tile, sides: ['right'] }];
  }
  if (state.turn !== player) return [];
  const out: LegalPlay[] = [];
  for (const d of state.hands[player]) {
    const sides: Side[] = [];
    if (canPlayLeft(state.chain, d)) sides.push('left');
    if (canPlayRight(state.chain, d)) sides.push('right');
    if (sides.length > 0) out.push({ domino: d, sides });
  }
  return out;
}

/** True iff the current player has no legal play AND cannot draw — they must pass. */
export function mustPass(state: DominoGameState): boolean {
  if (state.winner !== null) return false;
  if (legalPlaysFor(state, state.turn).length > 0) return false;
  if (state.mode === 'draw' && state.boneyard.length > 0) return false;
  return true;
}

/** True iff the current player has no legal play but a draw is available. */
export function mustDraw(state: DominoGameState): boolean {
  if (state.winner !== null) return false;
  if (state.mode !== 'draw') return false;
  if (state.boneyard.length === 0) return false;
  return legalPlaysFor(state, state.turn).length === 0;
}

function removeFromHand(hand: Domino[], dominoId: string): Domino[] {
  const out = hand.slice();
  const idx = out.findIndex((d) => d.id === dominoId);
  if (idx === -1) throw new Error(`hand does not contain ${dominoId}`);
  out.splice(idx, 1);
  return out;
}

function withTurnAdvanced(state: DominoGameState, passes: number): DominoGameState {
  return { ...state, turn: (1 - state.turn) as Player, passes };
}

function checkWinAfterPlay(state: DominoGameState, player: Player): DominoGameState {
  if (state.hands[player].length === 0) {
    return { ...state, winner: player, result: 'empty' };
  }
  return state;
}

function checkBlock(state: DominoGameState): DominoGameState {
  if (state.passes < 2) return state;
  const totals: [number, number] = [
    handPipTotal(state.hands[0]),
    handPipTotal(state.hands[1]),
  ];
  if (totals[0] === totals[1]) {
    return { ...state, winner: null, result: 'draw' };
  }
  const winner: Player = totals[0] < totals[1] ? 0 : 1;
  return { ...state, winner, result: 'blocked' };
}

export function tryPlay(state: DominoGameState, dominoId: string, side: Side): DominoGameState {
  if (state.winner !== null) return state;
  const player = state.turn;
  const legal = legalPlaysFor(state, player).find((p) => p.domino.id === dominoId);
  if (!legal) throw new Error(`illegal play: ${dominoId}`);
  if (!legal.sides.includes(side)) {
    throw new Error(`domino ${dominoId} cannot be played on ${side}`);
  }
  const tile = legal.domino;
  const chain = side === 'left'
    ? chainPlayLeft(state.chain, tile)
    : chainPlayRight(state.chain, tile);

  let next: DominoGameState = {
    ...state,
    chain,
    hands: state.turn === 0
      ? [removeFromHand(state.hands[0], dominoId), state.hands[1]]
      : [state.hands[0], removeFromHand(state.hands[1], dominoId)],
    requiredOpener: null,
    passes: 0,
  };
  next = checkWinAfterPlay(next, player);
  if (next.winner !== null) return next;
  return withTurnAdvanced(next, 0);
}

/**
 * Draw one tile from the boneyard into the current player's hand. Turn
 * does NOT advance — the player keeps drawing (or finally plays / passes).
 */
export function tryDraw(state: DominoGameState): DominoGameState {
  if (state.winner !== null) return state;
  if (state.mode !== 'draw') {
    throw new Error('tryDraw is only valid in draw mode');
  }
  if (state.boneyard.length === 0) {
    throw new Error('boneyard is empty');
  }
  const drawn = state.boneyard[0]!;
  const newBoneyard = state.boneyard.slice(1);
  const newHand = state.hands[state.turn].slice();
  newHand.push(drawn);
  const newHands: [Domino[], Domino[]] = state.turn === 0
    ? [newHand, state.hands[1]]
    : [state.hands[0], newHand];
  return { ...state, boneyard: newBoneyard, hands: newHands };
}

export function tryPass(state: DominoGameState): DominoGameState {
  if (state.winner !== null) return state;
  if (!mustPass(state)) {
    throw new Error('cannot pass: a legal play or draw exists');
  }
  const passes = state.passes + 1;
  const advanced = withTurnAdvanced(state, passes);
  return checkBlock(advanced);
}

/** Convenience: total pip count remaining in a player's hand. */
export function handTotal(state: DominoGameState, player: Player): number {
  return handPipTotal(state.hands[player]);
}

// re-export for callers building game state outside this file
export { makeDomino, isDouble };
